import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Table } from "reactstrap";
import {
  BASE_URL,
  getData,
  handleErrors,
  S3_PUBLIC_URL,
  postData,
} from "../../../../config";
import {
  FileManagerButton,
  FileManagerImg,
  FileManagerTableRow,
  FileManagerSpan,
  FileManagerThead,
} from "../../common/StyledComponents";
import {
  bytesToSize,
  formatDate,
} from "../../../../helpers/format";
import Spinner from "../../common/Spinner";

const readFileAsRawBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unable to read file"));
        return;
      }
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export default ({
  show,
  toggleFileManager,
  eid,
  selectImage,
  selectedClient,
}) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clientImages, setClientImages] = useState([]);

  const hiddenUploadImageInput = useRef();

  const sortImagesByDate = (a, b) => {
    if (new Date(a.date).getTime() > new Date(b.date).getTime()) return -1;
    else return 1;
  };

  const uploadFile = async (event) => {
    const file = event.target.files[0];
    if (file !== undefined && file !== null) {
      if (file.size > 517200) {
        console.log("exceeded 500 KB"); // TODO: show error
        return;
      } else {
        // Same context as listing: selectedClient => exhibitor routes, else expo via eid.
        const isClientUpload = Boolean(selectedClient);
        if (!isClientUpload && !eid) {
          console.log("[FileManager] upload skipped: missing selectedClient and eid");
          return;
        }
        const uploadUrl = isClientUpload
          ? `${BASE_URL}/api/email_editor/images/exhibitor/${selectedClient}/upload`
          : `${BASE_URL}/api/email_editor/images/${eid}/upload`;
        console.log("[FileManager] upload start", {
          mode: isClientUpload ? "client" : "expo",
          selectedClient: selectedClient || null,
          eid,
          uploadUrl,
          filename: file.name,
          contentType: file.type,
        });
        setLoading(true);
        try {
          const data = await readFileAsRawBase64(file);
          console.log("[FileManager] file read", {
            filename: file.name,
            bytes: file.size,
            base64Length: data ? data.length : 0,
          });
          const response = await fetch(
            uploadUrl,
            postData({
              filename: file.name,
              contentType: file.type,
              data,
            })
          );
          console.log("[FileManager] upload response", {
            ok: response.ok,
            status: response.status,
            url: response.url,
          });
          const ret = await handleErrors(response).json();
          console.log("[FileManager] upload parsed", { key: ret && ret.key });
          if (!ret || !ret.key) {
            throw new Error("Upload did not return a key");
          }
          toggleFileManager();
          console.log("[FileManager] selectImage", ret.key);
          selectImage(ret.key);
          if (isClientUpload) {
            console.log("[FileManager] register client", {
              selectedClient,
              key: ret.key,
            });
            await addImageToClient(ret.key);
          } else {
            console.log("[FileManager] register expo", { eid, key: ret.key });
            await addImageToExpo(ret.key);
          }
          console.log("[FileManager] upload flow done");
        } catch (err) {
          console.log("[FileManager] upload flow error", err);
          setLoading(false);
        }
      }
    }
  };

  const handleUploadClick = () => {
    hiddenUploadImageInput.current.click();
  };

  const fetchExpoImages = useCallback(async () => {
    setLoading(true);
    await fetch(`${BASE_URL}/api/email_editor/images/${eid}`, getData)
      .then(handleErrors)
      .then((response) => response.json())
      .then((ret) => {
        setImages(ret);
      })
      .catch(console.log);
    setLoading(false);
  }, [eid]);

  const fetchClientImages = useCallback(async () => {
    if (selectedClient) {
      setLoading(true);
      await fetch(
        `${BASE_URL}/api/email_editor/images/exhibitor/${selectedClient}`,
        getData
      )
        .then(handleErrors)
        .then((response) => response.json())
        .then((ret) => {
          setClientImages(ret);
        })
        .catch(console.log);
      setLoading(false);
    }
  }, [selectedClient]);

  const addImageToClient = async (key) => {
    const url = `${BASE_URL}/api/email_editor/images/exhibitor/${selectedClient}`;
    console.log("[FileManager] addImageToClient POST", { url, key });
    await fetch(url, postData({ key }))
    .then(handleErrors)
    .then(() => {
      console.log("[FileManager] addImageToClient ok, refreshing lists");
      fetchExpoImages();
      fetchClientImages();
    }).catch((err) => {
      console.log("[FileManager] addImageToClient error", err);
    })
  }
  const addImageToExpo = async (key) => {
    const url = `${BASE_URL}/api/email_editor/images/${eid}`;
    console.log("[FileManager] addImageToExpo POST", { url, key });
    await fetch(url, postData({ key }))
      .then(handleErrors)
      .then(() => {
        console.log("[FileManager] addImageToExpo ok, refreshing lists");
        fetchExpoImages();
        fetchClientImages();
      })
      .catch((err) => {
        console.log("[FileManager] addImageToExpo error", err);
      });
  };

  const removeImage = async (event, image) => {
    event.stopPropagation()
    const data = {
      image: image.name,
      expo_id: eid,
    };
    if (selectedClient) data.company_id = selectedClient;
    await fetch(
      `${BASE_URL}/api/email_editor/images/${eid}/remove`,
      postData(data)
    )
      .then(handleErrors)
      .then(() => {
        fetchExpoImages();
        fetchClientImages();
      })
      .catch((err) => {});
  };

  useEffect(() => {
    fetchExpoImages();
  }, [fetchExpoImages]);

  useEffect(() => {
    fetchClientImages();
  }, [fetchClientImages]);

  return (
    <Modal
      isOpen={show}
      toggle={toggleFileManager}
      className="file-manager-modal"
    >
      <ModalHeader toggle={toggleFileManager}>
        <Row>
          <Col>File manager</Col>
        </Row>
        <Row>
          <Col>
            <FileManagerButton
              onClick={() => {
                fetchExpoImages();
                fetchClientImages();
              }}
              size="sm"
              outline
              color="info"
            >
              <i className="fa fa-refresh mx-1" aria-hidden="true" />
            </FileManagerButton>{" "}
            {/* <FileManagerButton size="sm" outline color="success"><i className="fa fa-folder mx-1" aria-hidden="true" /></FileManagerButton>{' '} */}
            <FileManagerButton
              onClick={handleUploadClick}
              size="sm"
              color="primary"
            >
              <span>
                Upload{" "}
                <i className="fa fa-cloud-upload mx-1" aria-hidden="true"></i>
              </span>
            </FileManagerButton>{" "}
          </Col>
        </Row>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <Spinner />
        ) : (
          <Row>
            <Col md="12" className="file-manager-modal-body">
              {images.length ? (
                <Table hover>
                  <FileManagerThead>
                    <tr>
                      <th></th>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Size</th>
                      <th>Type</th>
                      <th></th>
                    </tr>
                  </FileManagerThead>
                  <tbody>
                    {[...images, ...clientImages]
                      .sort(sortImagesByDate)
                      .map((img) => (
                        <FileManagerTableRow
                          key={img.name}
                          onClick={() => selectImage(img.name)}
                        >
                          <th>
                            <FileManagerImg
                              src={`${S3_PUBLIC_URL}/${img.name}`}
                            />
                          </th>
                          <th>
                            <FileManagerSpan>{img.name}</FileManagerSpan>
                          </th>
                          <th>
                            <FileManagerSpan>
                              {formatDate(img.date)}
                            </FileManagerSpan>
                          </th>
                          <th>
                            <FileManagerSpan>
                              {bytesToSize(img.size)}
                            </FileManagerSpan>
                          </th>
                          <th>
                            <FileManagerSpan>{img.type}</FileManagerSpan>
                          </th>
                          <th>
                            <FileManagerButton
                              onClick={(e) => removeImage(e, img)}
                              size="sm"
                              outline
                              color="info"
                            >
                              <i
                                className="fa fa-trash mx-1"
                                aria-hidden="true"
                              />
                            </FileManagerButton>{" "}
                          </th>
                        </FileManagerTableRow>
                      ))}
                  </tbody>
                </Table>
              ) : (
                <></>
              )}
            </Col>
          </Row>
        )}
        <input
          ref={hiddenUploadImageInput}
          type="file"
          accept="image/png, image/jpeg"
          onChange={uploadFile}
          style={{ display: "none" }}
        />
      </ModalBody>
    </Modal>
  );
};
